import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BookMarked,
  FileCheck2,
  Languages,
  Building2,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { publicApi } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChatFloatingButton from "../components/ChatFloatingButton";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/dde13f54-cf57-488d-95a4-a46d55ab67c7/images/d63738f0cb0acd6086453c0fffd37ac247152325f72c2834fa0bcc5bdf7b8c66.png";
const LEGAL_IMG =
  "https://images.pexels.com/photos/7876093/pexels-photo-7876093.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { t } = useI18n();
  const [stats, setStats] = useState({ documents: 0, chunks: 0 });

  useEffect(() => {
    publicApi.publicStats().then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Header />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#F8F9FA]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.18]"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F8F9FA]/60 to-[#F8F9FA]" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 mb-6 px-3 py-1 border border-primary/20 bg-white/70 backdrop-blur rounded-sm">
                <Sparkles size={13} className="text-accent" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary font-semibold">
                  RAG · Cameroun · 2026
                </span>
              </div>

              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.95] text-foreground">
                <span className="text-primary">{t("tagline")}</span>
              </h1>

              <p className="mt-7 text-lg md:text-xl text-foreground/70 leading-relaxed max-w-2xl font-body">
                {t("subTagline")}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/chat"
                  className="group inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3.5 rounded-sm font-medium text-sm hover:bg-primary-light transition-all shadow-sm"
                  data-testid="cta-chat-btn"
                >
                  {t("hero.ctaChat")}
                  <ArrowUpRight
                    size={16}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </Link>
                <Link
                  to="/pricing"
                  className="group inline-flex items-center justify-center gap-2 bg-white border border-primary/20 text-primary px-6 py-3.5 rounded-sm font-medium text-sm hover:border-accent hover:bg-accent/5 transition-all"
                  data-testid="cta-pricing-btn"
                >
                  {t("nav.pricing")}
                  <ChevronRight size={16} />
                </Link>
                <Link
                  to="/admin/login"
                  className="group inline-flex items-center justify-center gap-2 bg-white border border-primary/20 text-primary px-6 py-3.5 rounded-sm font-medium text-sm hover:border-primary hover:bg-primary/5 transition-all"
                  data-testid="cta-admin-btn"
                >
                  {t("hero.ctaAdmin")}
                  <ChevronRight size={16} />
                </Link>
              </div>

              {/* Stats */}
              <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
                <StatBlock
                  value={stats.documents}
                  label={t("hero.stat1")}
                  testid="stat-docs"
                />
                <StatBlock
                  value={stats.chunks}
                  label={t("hero.stat2")}
                  testid="stat-chunks"
                />
                <StatBlock
                  value="99.9%"
                  label={t("hero.stat3")}
                  testid="stat-uptime"
                />
              </div>
            </div>

            {/* Side card */}
            <div className="lg:col-span-4 relative hidden lg:block">
              <div className="relative bg-white border border-primary/15 rounded-sm shadow-lg overflow-hidden">
                <div
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url(${LEGAL_IMG})` }}
                />
                <div className="p-5 border-t border-primary/10">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mb-2">
                    / corpus officiel
                  </div>
                  <div className="font-display text-xl text-primary font-semibold leading-tight mb-2">
                    Lois · Décrets · Arrêtés
                  </div>
                  <p className="text-sm text-foreground/70 leading-relaxed">
                    Textes de 2004 à nos jours, indexés article par article avec
                    citations juridiques exactes.
                  </p>
                </div>
              </div>
              <div className="absolute -top-3 -right-3 bg-accent text-[#0D1B2A] font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm shadow">
                CORPUS
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-20 md:py-28 border-t border-primary/10 bg-white" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-14">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent font-semibold mb-3">
              / 01 — fonctionnalités
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
              {t("pillars.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Pillar
              icon={BookMarked}
              title={t("pillars.p1Title")}
              desc={t("pillars.p1Desc")}
              number="01"
            />
            <Pillar
              icon={FileCheck2}
              title={t("pillars.p2Title")}
              desc={t("pillars.p2Desc")}
              number="02"
            />
            <Pillar
              icon={Languages}
              title={t("pillars.p3Title")}
              desc={t("pillars.p3Desc")}
              number="03"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Floating Chat Button */}
      <ChatFloatingButton />
    </div>
  );
}

function StatBlock({ value, label, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-display text-3xl md:text-4xl font-semibold text-primary">
        {value}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mt-1">
        {label}
      </div>
    </div>
  );
}

function Pillar({ icon: Icon, title, desc, number }) {
  return (
    <div className="group relative bg-white border border-primary/15 rounded-sm p-7 hover:-translate-y-1 hover:border-accent transition-all duration-200 overflow-hidden">
      <div className="absolute top-3 right-4 font-mono text-[11px] text-foreground/30 group-hover:text-accent transition-colors">
        / {number}
      </div>
      <div className="w-11 h-11 bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-white transition-colors">
        <Icon size={19} strokeWidth={1.7} />
      </div>
      <h3 className="font-display text-xl font-semibold text-foreground mb-2">
        {title}
      </h3>
      <p className="text-[14px] text-foreground/70 leading-relaxed">{desc}</p>
    </div>
  );
}
